/**
 * @voltxampere/audio — Buzzer state mapping (Sprint 31-5)
 *
 * Buzzer behaves like a binary tone: silent below `V_threshold`, fixed
 * 2 kHz square wave above. Unlike Speaker (variable V → Hz log map),
 * Buzzer has no frequency variation — real piezo buzzers are tuned to
 * a single resonant peak.
 *
 * The Tone.js engine wrapper sets `Oscillator.type = "square"` when
 * the AudioFrame entry's `type === "buzzer"` (Sprint 31-1
 * tone-engine.ts already branches on this). The mapping below packs
 * V_pin into the same `ComponentAudioState` shape Speaker uses, just
 * with frequency hard-coded.
 *
 * Sentinel rows pinned by tests:
 *   |V_pin| <  1 V  → active false, frequency 0, volume 0
 *   |V_pin| =  1 V  → active true (boundary inclusive), frequency 2000
 *   |V_pin| >  1 V  → active true, frequency 2000, volume 0.5
 *   negative V_pin  → symmetric (rectifier-like, polarity ignored)
 *
 * Volume is FIXED at 0.5 — buzzers don't have variable loudness;
 * piezo crystal output is binary on/off at the resonant peak. Master
 * volume slider (Sprint 31-6) still controls global loudness.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
const V_BUZZER_THRESHOLD = 1.0;
const BUZZER_FREQUENCY = 2000;
const BUZZER_VOLUME = 0.5;
/**
 * True iff buzzer is currently beeping (|V_pin| at/above threshold).
 * Boundary inclusive — exactly 1 V triggers the buzzer (real piezo
 * datasheets typically spec >= V_min, not strictly greater).
 */
export function buzzerActive(V_pin) {
    return Math.abs(V_pin) >= V_BUZZER_THRESHOLD;
}
/**
 * Buzzer's fixed resonant frequency. Returns 0 when the buzzer is
 * silent so the caller can skip oscillator construction (matches
 * Speaker's `speakerFrequency` zero-when-silent contract).
 */
export function buzzerFrequency(V_pin) {
    return buzzerActive(V_pin) ? BUZZER_FREQUENCY : 0;
}
/**
 * Buzzer's fixed loudness. Returns 0 when silent, BUZZER_VOLUME (0.5)
 * when active. Master volume slider scales this further.
 */
export function buzzerVolume(V_pin) {
    return buzzerActive(V_pin) ? BUZZER_VOLUME : 0;
}
//# sourceMappingURL=buzzer-state.js.map